import { createAsyncThunk } from "@reduxjs/toolkit";
import i18next from "i18next";
import { toast } from "react-toastify";
import { apiCall } from "services/api";

import {
  VotingFormData,
  VotingNoteSaveData,
  VotingNoteUpdateData,
  VotingStatus,
  VotingsFilters,
} from "store/voting/types";

import { toFormData } from "utils/files";
import { getDownloadVotingSignedSummaryUrl } from "utils/voting";

export const getUserVotings = createAsyncThunk(
  "/votings/user",
  async (filters: VotingsFilters, { rejectWithValue }) => {
    try {
      const response = await apiCall().get("/my/votings", { params: filters });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getAllVotings = createAsyncThunk(
  "/votings/all",
  async (filters: VotingsFilters, { rejectWithValue }) => {
    try {
      const response = await apiCall().get("/votings", { params: filters });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getVoting = createAsyncThunk(
  "/votings/get-single",
  async (
    {
      votingId,
      params,
    }: {
      votingId: string | number;
      params?: {
        seen: Date;
      };
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().get(`/votings/${votingId}`, { params });
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const postVoting = createAsyncThunk(
  "/votings/post",
  async (values: VotingFormData, { rejectWithValue }) => {
    try {
      const response = await apiCall().post(`/votings`, values);

      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "voting.actionMessages.created"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const putVoting = createAsyncThunk(
  "/votings/put",
  async (values: VotingFormData, { rejectWithValue }) => {
    try {
      const response = await apiCall().put(`/votings/${values.id}`, values);
      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "voting.actionMessages.updated"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteVoting = createAsyncThunk(
  "/votings/delete",
  async (votingId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall().delete(`/votings/${votingId}`);
      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "voting.actionMessages.deleted"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const submitVote = createAsyncThunk(
  "/votings/submit-vote",
  async (
    values: {
      votingId: number;
      options: { id: number; shares?: number }[];
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().post(
        `/my/votings/${values.votingId}/vote`,
        {
          options: values.options,
        }
      );
      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "voting.actionMessages.voteSubmitted"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const startVoting = createAsyncThunk(
  "/votings/start",
  async (votingId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall().post(`/votings/${votingId}/start`);
      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "voting.actionMessages.started"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const endVoting = createAsyncThunk(
  "/votings/end",
  async (votingId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall().post(`/votings/${votingId}/end`);
      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "voting.actionMessages.ended"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const getVotingQuorumTypes = createAsyncThunk(
  "/votings/quorum-types",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall().get(
        "/enums/single?enum=Domain\\Quorum\\Enums\\VotingQuorumType"
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getVotingTypes = createAsyncThunk(
  "/votings/voting-types",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall().get(
        "/enums/single?enum=Domain\\Voting\\Enums\\VotingType"
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getVotingSecrecy = createAsyncThunk(
  "/votings/voting-secrecy",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall().get(
        "/enums/single?enum=Domain\\Voting\\Enums\\VotingResultsSecrecy"
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const createMeetingVoting = createAsyncThunk(
  "/meetings/voting/post",
  async (
    data: { values: VotingFormData; meetingId: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().post(
        `/meetings/${data.meetingId}/votings`,
        data.values
      );

      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "voting.actionMessages.created"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteMeetingVoting = createAsyncThunk(
  "/votings/meetings/:id/delete",
  async (
    values: { meetingId: number; votingId: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().delete(
        `/meetings/${values.meetingId}/votings/${values.votingId}`
      );
      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "voting.actionMessages.deleted"
        )
      );
      return { response: response.data, votingId: values.votingId };
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const setVotingAttendance = createAsyncThunk(
  "/votings/attendance",
  async (
    values: {
      votingId: number;
      status: "accepted" | "rejected";
      reason?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().post(
        `/my/votings/${values.votingId}/invitation`,
        { invitation_status: values.status, reason: values?.reason || "" }
      );
      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "voting.actionMessages.statusUpdated"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const transferVote = createAsyncThunk(
  "/votings/transfer-vote",
  async (
    values: {
      votingId: number;
      userId: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().post(
        `/my/votings/${values.votingId}/ballots/transfer/${values.userId}`
      );
      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "voting.actionMessages.voteTransferred"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const getUserBallots = createAsyncThunk(
  "/votings/my-ballots",
  async (votingId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall().get(`/my/votings/${votingId}/ballots`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const abstainFromVoting = createAsyncThunk(
  "/votings/abstain",
  async (votingId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall().post(
        `/my/votings/${votingId}/vote/abstain`
      );
      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "voting.actionMessages.abstained"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const getVotingAccess = createAsyncThunk(
  "/votings/access",
  async (votingId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall({}).get(`/votings/${votingId}/room`);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const putVotingNote = createAsyncThunk(
  "/votings/note/put",
  async (data: VotingNoteUpdateData, { rejectWithValue }) => {
    try {
      const response = await apiCall().put(
        `/votings/${data.votingId}/note/${data.noteId}`,
        data
      );
      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "voting.actionMessages.noteSaved"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const saveVotingNote = createAsyncThunk(
  "/votings/note/post",
  async (data: VotingNoteSaveData, { rejectWithValue }) => {
    try {
      const response = await apiCall().post(`/votings/${data.votingId}/note`, {
        note: data.note,
      });
      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "voting.actionMessages.noteSaved"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const requestVotingSummaryApprove = createAsyncThunk(
  "/voting/request-approve",
  async (
    values: {
      votingId: number;
      users: number[];
      approval_deadline: string | null;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().post(
        `/votings/${values.votingId}/request-approval`,
        {
          users: values.users,
          approval_deadline: values.approval_deadline,
        }
      );

      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "votingSummary.approvalRequestedAction"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const approveOrRejectVotingSummary = createAsyncThunk(
  "/voting/approve",
  async (
    values: {
      votingId: number;
      comment?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().patch(
        `/votings/${values.votingId}/approve`,
        values.comment
          ? {
              comment: values.comment,
              status: VotingStatus.REJECTED,
            }
          : {
              status: VotingStatus.ACCEPTED,
            }
      );

      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          values.comment
            ? "votingSummary.disapprovedAction"
            : "votingSummary.approvedAction"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const downloadVotingSummaryPdf = createAsyncThunk(
  "/voting/download-pdf",
  async (votingId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall().get(
        `/votings/${votingId}/summary/download`
      );

      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "votingSummary.downloadedAction"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const uploadSignedVotingSummary = createAsyncThunk(
  "/voting/upload-signed-summary",
  async (
    values: {
      votingId: number;
      file: File;
    },
    { rejectWithValue }
  ) => {
    const formData = toFormData({ file: values.file });
    try {
      const response = await apiCall().post(
        `/votings/${values.votingId}/summary/upload-signed-summary`,
        formData
      );

      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "votingSummary.signedAction"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateVotingSummaryConclusion = createAsyncThunk(
  "/voting/download-pdf",
  async (
    values: {
      votingId: number;
      conclusion: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().patch(
        `/votings/${values.votingId}/conclusion`,
        {
          conclusion: values.conclusion,
        }
      );

      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "votingSummary.conclusionUpdated"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const downloadVotingSignedSummaryPdf = createAsyncThunk(
  "/voting/download-signed-summary",
  async (
    {
      votingId,
      signitRequestId,
    }: {
      votingId: number;
      signitRequestId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().get(
        getDownloadVotingSignedSummaryUrl(votingId, signitRequestId)
      );

      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "votingSummary.downloadedAction"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const uploadVotingSummaryPdf = createAsyncThunk(
  "/voting/upload-summary-pdf",
  async (
    {
      votingId,
      file,
      latestPage,
    }: { votingId: number; file: Blob; latestPage: number },
    { rejectWithValue }
  ) => {
    const formData = toFormData({
      file: file,
      latestPage: latestPage,
    });
    try {
      const response = await apiCall().post(
        `/votings/${votingId}/summary/upload`,
        formData
      );

      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);
export const getSignitEmbeddedUrl = createAsyncThunk(
  "/voting-minutes/signit-embedded-url",
  async (
    values: {
      signit_signatory_id: string;
      signit_signature_request_id: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().post(
        `/summary/signit/embedded-url`,
        values
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);
export const remindToVote = createAsyncThunk(
  "/votings/transfer-vote",
  async (
    values: {
      votingId: number;
      userId: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().post(
        `/votings/${values.votingId}/remind/${values.userId}`
      );
      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "voting.actionMessages.remindNotificationSent"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const uploadVotingFile = createAsyncThunk(
  "/votings/attachment/add",
  async (
    {
      votingId,
      file,
    }: { votingId: string; file: { attachment: File; description: string } },
    { rejectWithValue }
  ) => {
    const formData = toFormData({
      attachment: file.attachment,
      description: file.description,
    });
    try {
      const response = await apiCall().post(
        `/votings/${votingId}/attachments`,
        formData
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);
export const getVotingFile = createAsyncThunk(
  "/votings/attachment/get",
  async (
    { votingId, attachmentId }: { votingId: string; attachmentId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().get(
        `/votings/${votingId}/attachments/${attachmentId}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);
export const deleteVotingFile = createAsyncThunk(
  "/votings/attachment/delete",
  async (
    { votingId, attachmentId }: { votingId: string; attachmentId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().delete(
        `/votings/${votingId}/attachments/${attachmentId}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const signSummary = createAsyncThunk(
  "/votings/sign-summary-pdf",
  async ({ votingId }: { votingId: number }, { rejectWithValue }) => {
    try {
      const response = await apiCall().post(
        `/votings/${votingId}/summary/signed-summary`
      );

      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);
export const downloadMeetingSignedSummaryPdf = createAsyncThunk(
  "/meeting-minutes/download-signed-pdf",
  async (
    {
      votingId,
      signitRequestId,
    }: {
      votingId: number;
      signitRequestId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().get(
        getDownloadVotingSignedSummaryUrl(votingId, signitRequestId)
      );

      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "meetingSummary.minutesDownloadedAction"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);
